import { useCallback, useEffect } from 'react'
import noop from 'lodash/noop'
import { useLocation } from 'react-router-dom'
import { environment } from '@chilipiper/config'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { trackChameleonEvent, usePostHog } from '@chilipiper/tracking'
import { fetchExtensionInstalled } from '@/api/extension'
import type {
  ChilicalEventName,
  ChilicalEventPayloads,
  ChilicalFeatureName,
} from '@/tracking/events'
import { getFeatureFromRoute } from '@/tracking/events'

/**
 * Fire a product-usage event with a mandatory `feature` grouping.
 *
 * The event name is checked against `ChilicalEventName` and the payload against that event's entry
 * in `ChilicalEventPayloads`, so a missing or misspelled property is a compile error rather than a
 * gap discovered in PostHog. Events typed `void` take no third argument.
 *
 * Usage: `const track = useTrackFeature(); track(ChilicalEvent.MeetingCancelled,
 * ChilicalFeature.Calendar, { meeting_id })`
 */
export const useTrackFeature = () => {
  const posthog = usePostHog()
  return useCallback(
    <E extends ChilicalEventName>(
      event: E,
      feature: ChilicalFeatureName,
      ...args: ChilicalEventPayloads[E] extends void ? [] : [properties: ChilicalEventPayloads[E]]
    ) => {
      const [properties] = args as [ChilicalEventPayloads[E]?]
      posthog?.capture(event, { feature, ...properties })
    },
    [posthog]
  )
}

/** Captures `$pageview`, and keeps the `feature` super property in step with the route. */
export const usePageviewTracking = () => {
  const posthog = usePostHog()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!posthog) {
      return
    }

    const feature = getFeatureFromRoute(pathname)

    if (feature) {
      posthog.register({ feature })
    } else {
      // Drop it, rather than leave the previous route's feature attached to an unmapped area
      posthog.unregister('feature')
    }

    // Must precede capture in this one effect, else $pageview carries the previous route's feature
    posthog.capture('$pageview')
  }, [pathname, posthog])
}

export const useExtensionInstalledTracking = () => {
  const { userId } = useFireSessionContext()
  useEffect(() => {
    if (userId && environment.node !== 'test') {
      fetchExtensionInstalled()
        .then(() => {
          trackChameleonEvent('Extension installed')
        })
        .catch(noop)
    }
  }, [])
}
